import { Ban, CheckCircle2, Package, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import {
  InventoryItemModal,
  type InventoryItemFormSection,
} from '../components/InventoryItemModal';
import { inventoryItemsApi } from '../inventoryApi';
import type { InventoryItemDetail } from '../inventoryTypes';
import { ITEM_KIND, ITEM_KIND_LABEL, LICENSING_MODEL_LABEL, OWNERSHIP_LABEL } from '../inventoryTypes';

type TabKey = 'info';

/** A quantity of zero is muted, like every other quantity in the product. */
function Quantity({ value }: { value: number }) {
  return (
    <span className={`font-mono tabular-nums ${value === 0 ? 'text-slate-300' : ''}`}>{value}</span>
  );
}

/**
 * One item in the catalog.
 *
 * On the **header** layout: a catalog row reads as one continuous thing rather than a record
 * whose content splits into panels a reader chooses between.
 *
 * **Three cards, three Edits, and each opens the form at its OWN section.** One card is one
 * modal over that card's fields and nothing else — a single form spanning every card would put
 * fields the reader did not come for in front of them and turn a one-field change into a review
 * of the whole record.
 */
export function InventoryItemProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [item, setItem] = useState<InventoryItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState<InventoryItemFormSection | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      inventoryItemsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setItem(next);

          // Cleared on success, or a failed restore keeps its banner over a page that has since
          // re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this item.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Puts a deleted item back.
   *
   * **No confirm tick and no password**: restoring puts something back, and ceremony on an undo
   * only teaches readers to click through ceremony. It also cannot collide — the SKU was never
   * freed, which is the whole point of that index being unfiltered.
   */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await inventoryItemsApi.activate(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this item.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Settings', to: '/inventory/settings' },
    { label: 'Items', to: '/inventory/settings/items' },
    item?.sku ?? 'Item',
  ];

  // Guarded on having NOTHING to show, never on `error` alone: once a record is up it is still
  // the last good answer, and a failed restore goes to the banner rather than blanking the page.
  if (error && !item) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !item) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const edit = (section: InventoryItemFormSection) => ({
    key: `edit-${section}`,
    label: 'Edit',
    permission: 'inventory-items.update',
    onClick: () => setEditing(section),
  });

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="inventory-item-profile"
        header={
          <ProfileHeader
            id="inventory-item-profile-header"
            icon={<Package className="h-6 w-6" aria-hidden="true" />}
            title={item.name}
            meta={[item.sku, item.inventoryCategoryName, ITEM_KIND_LABEL[item.kind]]}
            secondary={[
              `Counted in ${item.unitName.toLowerCase()}`,
              item.tracksSerial ? 'Tracked by serial' : 'Tracked by quantity',
              item.tracksExpiry ? 'Expires' : 'Does not expire',
            ]}
            status={
              <StatusText tone={item.isActive ? 'active' : 'inactive'}>
                {item.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={item.createdByName}
            createdAt={item.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="inventory-item-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : item.isActive ? (
            <ProfileBanner
              id="inventory-item-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This item is offered: it appears on every stock form. On-hand stock is not held here — it is the sum of the stock rows that point at this item, which is a surface this module does not have yet."
            />
          ) : (
            <ProfileBanner
              id="inventory-item-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This item is deleted. It is no longer offered on a stock form, and its SKU stays permanently held — nothing else can ever take it."
              action={
                // Gates the button, not the banner: a read-only viewer still needs the state.
                <PermissionGate permission="inventory-items.delete">
                  <Button
                    id="inventory-item-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard id="inventory-item-details" title="Details" menuItems={[edit('details')]}>
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'SKU', value: item.sku, mono: true },
              { label: 'Name', value: item.name },
              { label: 'Category', value: item.inventoryCategoryName },
              { label: 'Kind', value: ITEM_KIND_LABEL[item.kind] },
              { label: 'Unit', value: `${item.unitName} (${item.unitCode})` },
            ]}
          />
        </ProfileCard>

        {/* Its own card, and `padded`, because a DOCUMENT does not draw its own edges - the
            precedent is the position profile's qualification standards. It shares the Details
            edit, which is the section the description is collected on. */}
        <ProfileCard
          padded
          id="inventory-item-description"
          title="Description"
          menuItems={[edit('details')]}
        >
          <RichTextDocument html={item.description} />
        </ProfileCard>

        <ProfileCard id="inventory-item-stock" title="Stock control" menuItems={[edit('stock')]}>
          <PreviewTable
            variant="table-display"
            rows={[
              /*
                NO unit-cost row — the catalog row stopped carrying one on 20 Sep 2026. A price
                belongs to the lot or the unit that was actually paid for, and what an item is
                worth in total reads on Stock Levels' Investment column.
              */
              { label: 'Reorder at', value: <Quantity value={item.reorderLevel} /> },
              {
                label: 'Maximum stock',
                value: item.maxStockLevel === null ? null : <Quantity value={item.maxStockLevel} />,
              },
              {
                label: 'Tracked',
                value: item.tracksSerial
                  ? 'By serial — each unit is identified individually'
                  : 'By quantity — counted in batches',
              },
              {
                /*
                  Exactly one of these two is shown, because an item spoils on a date or wears
                  out over a span and never both. Rendering the other as an em dash would offer
                  a fact the record cannot have.
                */
                label: item.tracksExpiry ? 'Expiry warning' : 'Estimated useful life',
                value: item.tracksExpiry ? (
                  <span className="font-mono tabular-nums">{item.expiryThresholdDays} days</span>
                ) : item.estimatedUsefulLifeYears === null ? null : (
                  <span className="font-mono tabular-nums">{item.estimatedUsefulLifeYears} years</span>
                ),
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard
          id="inventory-item-identity"
          title="Product identity"
          menuItems={[edit('identity')]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Manufacturer/Service Provider', value: item.manufacturer },

              /*
                The kind decides which of these the record can hold, so the other is not drawn
                at all. An em dash would state that the item has no model when the question was
                never asked of it.
              */
              ...(item.kind === ITEM_KIND.consumable
                ? [{ label: 'Brand', value: item.brandName }]
                : [{ label: 'Model', value: item.model }]),

              ...(item.kind === ITEM_KIND.subscription
                ? [
                    {
                      label: 'Licensing',
                      value: item.licensingModel === null ? null : LICENSING_MODEL_LABEL[item.licensingModel],
                    },
                  ]
                : []),

              ...(item.kind === ITEM_KIND.asset
                ? [
                    {
                      label: 'Ownership type',
                      value: item.ownership === null ? null : OWNERSHIP_LABEL[item.ownership],
                    },
                  ]
                : []),
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="inventory-item-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: item.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(item.createdAt)} className="font-mono">
                    {formatDateTime(item.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: item.updatedByName },
              {
                label: 'Updated',
                value: item.updatedAt ? (
                  <time dateTime={toIso(item.updatedAt)} className="font-mono">
                    {formatDateTime(item.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <InventoryItemModal
          open
          editing={item}
          section={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
